import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MessageCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { ModernLoader } from '@/components/ui/modern-loader';

interface Conversation {
  userId: string;
  username: string;
  avatarUrl?: string;
  lastMessage: string;
  lastMessageTime: string;
  unreadCount: number;
  videoId?: string;
}

export default function Inbox() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && user) {
      console.log('[Inbox] User authenticated, fetching data...');
      fetchConversations();
      setupRealtimeSubscription();
    } else if (!authLoading && !user) {
      console.log('[Inbox] No user found, stopping loading');
      setLoading(false);
    }
  }, [user, authLoading]);

  const setupRealtimeSubscription = () => {
    if (!user) return;

    const messagesChannel = supabase
      .channel('inbox_messages')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `recipient_id=eq.${user.id}`,
        },
        () => {
          fetchConversations();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `sender_id=eq.${user.id}`,
        },
        () => {
          fetchConversations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(messagesChannel);
    };
  };

  const fetchConversations = async () => {
    if (!user) {
      console.log('[Inbox] No user for fetchConversations');
      return;
    }

    try {
      console.log('[Inbox] Fetching conversations for user:', user.id);
      
      // DON'T mark messages as read here - only when opening specific thread
      
      const { data: messages, error } = await supabase
        .from('messages')
        .select('*, video_id')
        .or(`sender_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[Inbox] Error fetching messages:', error);
        setConversations([]);
        return;
      }

      console.log('[Inbox] Messages fetched:', messages?.length || 0);

      const conversationMap = new Map<string, Conversation>();

      for (const message of messages || []) {
        const otherUserId = message.sender_id === user.id ? message.recipient_id : message.sender_id;
        
        if (!conversationMap.has(otherUserId)) {
          const { data: profile, error: profileError } = await supabase
            .from('public_profile_data')
            .select('username, avatar_url')
            .eq('id', otherUserId)
            .maybeSingle();

          if (profileError) {
            console.error('[Inbox] Error fetching profile:', profileError);
          }

          // Count actual unread messages from this user
          const unreadCount = messages.filter(m => 
            m.sender_id === otherUserId && 
            m.recipient_id === user.id && 
            !m.read
          ).length;

          conversationMap.set(otherUserId, {
            userId: otherUserId,
            username: profile?.username || 'Unknown User',
            avatarUrl: profile?.avatar_url || undefined,
            lastMessage: message.content,
            lastMessageTime: message.created_at,
            unreadCount,
            videoId: message.video_id || undefined,
          });
        }
      }

      const conversationsArray = Array.from(conversationMap.values());
      console.log('[Inbox] Conversations built:', conversationsArray.length);
      setConversations(conversationsArray);
    } catch (err) {
      console.error('[Inbox] Unexpected error in fetchConversations:', err);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  const parseMessagePreview = (content: string) => {
    // Remove URLs from links, keeping only the link text for preview
    return content
      .replace(/View here → \/video\/[^\s]+/g, 'View here →')
      .replace(/View video → \/video\/[^\s]+/g, 'View video →')
      .replace(/View Post → \/category\/[^\s#]+(?:#post-[^\s]+)?/g, 'View Post →')
      .replace(/View here → \/category\/[^\s#]+(?:#post-[^\s]+)?/g, 'View here →');
  };

  const totalUnreadMessages = conversations.reduce((acc, c) => acc + c.unreadCount, 0);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <ModernLoader />
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <SEO title="Inbox" description="View your messages and notifications" />
        <Header />
        <div className="min-h-screen flex items-center justify-center pt-24">
          <Card className="w-full max-w-md">
            <CardContent className="p-6 text-center">
              <p className="mb-4">Please sign in to view your inbox</p>
              <Button onClick={() => navigate('/auth')}>Sign In</Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO 
        title="Inbox - Messages & Notifications"
        description="View your messages and notifications"
      />
      <Header />
      <main className="min-h-screen bg-background pt-24 pb-8">
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold">Messages</h1>
            {totalUnreadMessages > 0 && (
              <Badge variant="destructive" className="h-6 min-w-6 rounded-full px-2">
                {totalUnreadMessages} unread
              </Badge>
            )}
          </div>

          <div className="space-y-3">
            {conversations.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <MessageCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No messages yet</p>
                </CardContent>
              </Card>
            ) : (
              conversations.map((conversation) => (
                <Card
                  key={conversation.userId}
                  className={`cursor-pointer transition-all hover:shadow-md hover:border-primary/50 ${
                    conversation.unreadCount > 0 ? 'border-primary/50 bg-primary/5' : ''
                  }`}
                  onClick={() => navigate(`/messages/${conversation.userId}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Avatar className="h-12 w-12 border-2 border-gold/20">
                        <AvatarImage src={conversation.avatarUrl} className="object-cover" />
                        <AvatarFallback className="bg-muted">
                          {conversation.username?.[0]?.toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <Link to={`/user/${conversation.username}`} className="hover:underline">
                            <p className={`font-semibold ${conversation.unreadCount > 0 ? 'text-primary' : ''}`}>
                              @{conversation.username}
                            </p>
                          </Link>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(conversation.lastMessageTime), { addSuffix: true })}
                            </span>
                            {conversation.unreadCount > 0 && (
                              <Badge variant="destructive" className="h-5 min-w-5 rounded-full px-1.5">
                                {conversation.unreadCount}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div>
                          <p className={`text-sm truncate ${
                            conversation.unreadCount > 0 ? 'font-medium text-foreground' : 'text-muted-foreground'
                          }`}>
                            {parseMessagePreview(conversation.lastMessage)}
                          </p>
                          {conversation.videoId && (
                            <Link 
                              to={`/video/${conversation.videoId}`}
                              className="text-xs text-primary hover:underline mt-1 inline-block"
                              onClick={(e) => e.stopPropagation()}
                            >
                              View video →
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
